import { useNavigate } from 'react-router-dom';
import { C, t } from '../constants/dashboardConstants';

const ORANGE = '#FF6B00';
const AD_YELLOW = '#FFC933';

const GRADIENT_PLACEHOLDER = {
  background: 'linear-gradient(135deg, #2a2a2a 0%, #1a1a1a 100%)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
};

function AdTag({ style }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', padding: '4px 10px',
      borderRadius: '5px', background: AD_YELLOW,
      fontFamily: 'Pretendard, sans-serif', fontSize: '12px', fontWeight: 700,
      letterSpacing: '-0.02em', color: '#090909', flexShrink: 0,
      ...style,
    }}>
      AD
    </span>
  );
}

function useGoToAd(ad) {
  const navigate = useNavigate();
  return () => {
    if (!ad) return;
    navigate(`/community/ads/${ad.id}`, { state: { category: 'Castar Ads' } });
  };
}

function AdImage({ src, alt, style }) {
  if (!src) {
    return <div style={{ ...GRADIENT_PLACEHOLDER, ...style }} />;
  }
  return <img src={src} alt={alt} style={{ objectFit: 'cover', display: 'block', ...style }} />;
}

/**
 * 배우찾기/공고찾기 카드 그리드에 끼워 넣는 광고 카드.
 * @param {import('../constants/ads').AdItem} props.ad
 * @param {'talent'|'audition'} props.variant - 배우찾기(사진 카드형) / 공고찾기(텍스트 카드형)
 */
export function AdGridCard({ ad, variant = 'talent' }) {
  const goToAd = useGoToAd(ad);
  if (!ad) return null;

  if (variant === 'audition') {
    return (
      <article
        onClick={goToAd}
        style={{
          background: C.white, border: `2px solid ${ORANGE}`, borderRadius: '20px',
          padding: '24px', display: 'flex', flexDirection: 'column', gap: '14px',
          cursor: 'pointer', transition: 'all 0.2s ease',
        }}
        onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 8px 24px rgba(255,107,0,0.18)'; }}
        onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <AdTag />
          <span style={{ ...t('14px', 500, C.sub) }}>이동 ›</span>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ ...t('18px', 700, C.bk), lineHeight: 1.4, marginBottom: '8px' }}>{ad.badge}</div>
          <div style={{ ...t('16px', 700, ORANGE), lineHeight: 1.4, marginBottom: '6px' }}>{ad.title}</div>
          <div style={{ ...t('13px', 400, C.sub) }}>{ad.advertiser} X 캐스타</div>
        </div>
      </article>
    );
  }

  // variant === 'talent': 정사각 사진 카드형 (TalentCard와 동일한 비율/톤)
  return (
    <div
      onClick={goToAd}
      style={{ background: C.white, borderRadius: '16px', overflow: 'hidden', border: `2px solid ${ORANGE}`, cursor: 'pointer', transition: 'all 0.2s ease' }}
      onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(255,107,0,0.18)'; }}
      onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
    >
      <div style={{ padding: '14px 14px 0' }}>
        <div style={{ ...t('12px', 500, C.sub), marginBottom: '4px' }}>{ad.advertiser} X 캐스타</div>
        <div style={{ ...t('17px', 700, ORANGE), lineHeight: 1.3 }}>{ad.badge}</div>
      </div>
      <div style={{ position: 'relative', width: '100%', aspectRatio: '1/1', overflow: 'hidden', margin: '10px 0' }}>
        <AdImage src={ad.gridImage || ad.images?.[0]} alt={ad.title} style={{ width: '100%', height: '100%' }} />
      </div>
      <div style={{ padding: '0 14px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ ...t('13px', 700, C.bk), marginBottom: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ad.badge}</div>
          <div style={{ ...t('12px', 400, C.sub), lineHeight: 1.4, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>{ad.subtitle}</div>
        </div>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}><path d="M9 18l6-6-6-6" stroke={C.sub} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
      </div>
    </div>
  );
}

/**
 * 제작사 대시보드 우측에 배치하는 세로형 광고 배너.
 */
export function AdSideBanner({ ad }) {
  const goToAd = useGoToAd(ad);
  if (!ad) return null;

  return (
    <div
      onClick={goToAd}
      style={{
        width: '100%', border: `2px solid ${ORANGE}`, borderRadius: '16px',
        overflow: 'hidden', cursor: 'pointer', background: C.white,
        display: 'flex', flexDirection: 'column',
      }}
    >
      <div style={{ padding: '20px 20px 0', textAlign: 'center' }}>
        <div style={{ ...t('13px', 600, C.sub), marginBottom: '8px' }}>{ad.advertiser} X 캐스타</div>
        <div style={{ ...t('19px', 800, ORANGE), lineHeight: 1.3 }}>{ad.badge}</div>
      </div>

      <div style={{ width: '100%', aspectRatio: '3/4', marginTop: '14px' }}>
        <AdImage src={ad.bannerImage || ad.images?.[0]} alt={ad.title} style={{ width: '100%', height: '100%' }} />
      </div>

      <div style={{ padding: '16px 20px 0', display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'center' }}>
        <span style={{ ...t('14px', 400, C.g400), textDecoration: 'line-through' }}>100,000원</span>
        <span style={{ ...t('24px', 800, ORANGE) }}>50,000원</span>
      </div>

      <div style={{ padding: '16px 20px 0' }}>
        <button
          onClick={(e) => { e.stopPropagation(); goToAd(); }}
          style={{ width: '100%', height: '48px', borderRadius: '10px', border: 'none', cursor: 'pointer', background: ORANGE, ...t('15px', 700, C.white) }}
        >
          문의하기 →
        </button>
      </div>

      <div style={{ padding: '10px 20px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ ...t('12px', 400, C.g400) }}>이벤트 기간: {ad.endDate}까지</span>
        <AdTag />
      </div>
    </div>
  );
}

/**
 * 커뮤니티 목록에서 쓰는 "Castar Ads" 뱃지 (list/grid 공용, 노란색).
 */
export function CommunityAdBadge() {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', padding: '4px 10px',
      borderRadius: '5px', background: AD_YELLOW,
      fontFamily: 'Pretendard, sans-serif', fontSize: '13px', fontWeight: 700,
      letterSpacing: '-0.02em', color: '#090909',
    }}>
      Castar Ads
    </span>
  );
}

export { AdTag, ORANGE };
